'use client'

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  RefObject,
  useMemo,
  Fragment,
  type ReactNode,
} from 'react'
import { usePathname } from 'next/navigation'
import AppIcon from '@/components/content/app/AppIcon'
import Popover from '@/components/ui/Popover'
import Sun from '@/icons/custom/sun'
import Moon from '@/icons/custom/moon'
import { Icon } from '@/icons/Icon'
import { cn, scrollToSection, toSlug } from '@/lib/utils'
import { usePageContext } from '@/components/content/common/PageContext'
import { useScrollGradients } from '@/hooks/useScrollGradients'
import { generateMenu } from '@/helpers/menus'
import { useDarkMode } from '@/hooks/useDarkMode'
import { generateStructure } from '@/lib/content'
import useMediaQuery from '@/hooks/useMediaQuery'
import type { MenuItem } from '@/helpers/menus'
import type { Block } from '@/types/app'
import type { App } from '@/payload-types'

export type ViewportSize = 'mobile' | 'tablet' | 'desktop'

type BlogPostSidebarProps = {
  content: Block[]
  apps: App[]
}

type SidebarContainerProps = {
  children: ReactNode
  className?: string
  scrollable?: boolean
}

type SidebarContentProps = {
  content: Block[]
}

type SidebarControlsProps = {
  apps: App[]
}

type SidebarButtonProps = {
  icon: ReactNode
  active?: boolean
  onClickAction?: () => void
}

type SidebarParagraphProps = {
  onClickAction?: () => void
  active?: boolean
}

export default function BlogPostSidebar({ content, apps }: BlogPostSidebarProps) {
  return (
    <>
      <div className="hide-scrollbar fixed right-3 top-[170px] z-30 hidden flex-col gap-2 sm:flex md:right-5 xl:right-6">
        <SidebarContainer className="gap-2.5">
          <SidebarControls apps={apps} />
        </SidebarContainer>

        <SidebarContainer>
          <SidebarContent content={content} />
        </SidebarContainer>
      </div>

      <div className="hide-scrollbar fixed bottom-0 left-0 z-40 flex h-20 w-full border-t border-black/5 bg-white/50 backdrop-blur-xl dark:border-white/10 dark:bg-white/10 sm:hidden">
        <SidebarContainer className="gap-2.5" scrollable={true}>
          <div className="flex gap-4">
            <SidebarControls apps={apps} />
          </div>
          <SidebarContent content={content} />
        </SidebarContainer>
      </div>
    </>
  )
}

export function SidebarControls({ apps }: SidebarControlsProps) {
  const { isDark, toggleTheme } = useDarkMode()

  const [menuVisible, setMenuVisible] = useState(false)

  const pathname = usePathname()

  const menu: MenuItem[] = useMemo(
    () =>
      generateMenu('app', {
        app: apps,
        pathname,
      }),
    [apps, pathname],
  )

  const handleMenuClose = useCallback(() => {
    setMenuVisible(false)
  }, [setMenuVisible])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuVisible) {
        handleMenuClose()
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleEscape)
      return () => window.removeEventListener('keydown', handleEscape)
    }
  }, [menuVisible, handleMenuClose])

  return (
    <>
      <Popover
        menu={menu}
        position="left"
        align="right"
        visible={menuVisible}
        setVisible={setMenuVisible}
      >
        <SidebarButton icon={<Icon name="dots" />} active={menuVisible} />
      </Popover>

      <SidebarButton icon={isDark ? <Moon /> : <Sun />} onClickAction={() => toggleTheme()} />
    </>
  )
}

export function SidebarContent({ content }: SidebarContentProps) {
  const { activeSection, setActiveSection } = usePageContext()
  const { isMobile, is2xl } = useMediaQuery()
  const { roots } = generateStructure({ content })

  const filteredRoots = roots.map((root) => ({
    ...root,
    children: root.children?.filter((child) => child.title) || [],
  }))

  const handleClick = async (slug: string) => {
    await scrollToSection({ id: slug, offset: 130, animate: true, hideHash: true })
    setActiveSection(slug)
  }

  return (
    <>
      {filteredRoots.map((root: any) => {
        const rootId = toSlug(root.title)
        return (
          <Fragment key={root.title}>
            <SidebarParagraph
              onClickAction={() => handleClick(rootId)}
              active={activeSection === rootId}
            />
            {root.children &&
              root.children.map((branch: any, index: number) => {
                const branchId = `${rootId}-${toSlug(branch.title)}`

                // Check if next item is an app
                const nextIsApp = index < root.children.length - 1 && branch.app

                if (branch.app) {
                  const appSlug = branch.app.slug || toSlug(branch.app.title)

                  return (
                    <div key={branch.title} className={cn(nextIsApp && 'mr-1.5 md:mb-2.5 md:mr-0')}>
                      <AppIcon
                        icon={branch.app.icon}
                        glyph={branch.app.glyph}
                        name={branch.app.name}
                        size={isMobile || is2xl ? 44 : 28}
                        invert={true}
                        background={branch.app.brandBackgroundColor || '#000000'}
                        active={activeSection === branchId}
                        onActivate={() => handleClick(appSlug)}
                        className={cn(
                          'cursor-pointer transition-transform duration-300',
                          !activeSection && 'hover:scale-90',
                        )}
                      />
                    </div>
                  )
                } else {
                  return (
                    <SidebarParagraph
                      key={branch.title}
                      onClickAction={() => handleClick(branchId)}
                      active={activeSection === branchId}
                    />
                  )
                }
              })}
          </Fragment>
        )
      })}
    </>
  )
}

export function SidebarContainer({ children, className, scrollable }: SidebarContainerProps) {
  const { activeSection } = usePageContext()
  const { isMobile } = useMediaQuery()

  const containerRef = useRef<HTMLDivElement>(null)
  const { showTopGradient, showBottomGradient } = useScrollGradients(
    containerRef as RefObject<HTMLElement>,
    isMobile,
  )

  useEffect(() => {
    if (activeSection) {
      const container = containerRef.current
      if (!container || !activeSection) return

      const selectedElement = container.querySelector(`[data-app-slug="${activeSection}"]`)
      if (!selectedElement) return

      if (isMobile) {
        const scrollLeft =
          selectedElement.getBoundingClientRect().left -
          container.getBoundingClientRect().left +
          container.scrollLeft -
          20
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
      } else {
        const scrollTop =
          selectedElement.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          12
        container.scrollTo({ top: scrollTop, behavior: 'smooth' })
      }
    }
  }, [activeSection, isMobile])

  return (
    <div className="relative h-full w-full sm:h-auto">
      <div
        className={cn('absolute inset-0 -z-10 backdrop-blur-xl sm:rounded-2xl 2xl:rounded-[20px]')}
      />
      <div
        className={cn(
          'relative h-full w-full sm:rounded-2xl sm:border sm:bg-gradient-to-b 2xl:rounded-[20px]',
          'before:pointer-events-none before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-8 before:bg-gradient-to-r before:to-transparent before:opacity-0 before:transition-opacity before:content-[""] sm:before:h-8 sm:before:w-full sm:before:bg-gradient-to-b',
          'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:z-10 after:h-full after:w-8 after:bg-gradient-to-l after:to-transparent after:opacity-0 after:transition-opacity after:content-[""] sm:after:h-8 sm:after:w-full sm:after:bg-gradient-to-t',
          'border-black/5 from-black/5 to-black/10 before:from-gray-50 after:from-gray-50 dark:border-white/20 dark:from-white/10 dark:to-white/15 dark:before:from-black dark:after:from-black',
          showTopGradient && 'before:opacity-100',
          showBottomGradient && 'after:opacity-100',
        )}
      >
        <div
          ref={containerRef}
          className={cn(
            'flex items-center p-5 sm:flex-col sm:p-2.5',
            scrollable &&
              'overflow-x-auto sm:max-h-[calc(100vh-140px)] sm:overflow-y-auto 2xl:max-h-[calc(100vh-160px)]',
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export function SidebarButton({ icon, active, onClickAction }: SidebarButtonProps) {
  return (
    <div
      onClick={onClickAction}
      className={cn(
        'flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center gap-3.5 rounded-xl outline outline-[1px] transition-all sm:h-7 sm:w-7 sm:rounded-lg 2xl:h-11 2xl:w-11 2xl:rounded-xl',
        active
          ? 'text-primary-500 shadow-icon-active outline-primary-500'
          : 'bg-white text-gray-950 outline-black/15 hover:outline-black/30 dark:bg-white/10 dark:text-white dark:outline-white/20 dark:hover:outline-white/30',
      )}
    >
      {icon}
    </div>
  )
}

export function SidebarParagraph({ onClickAction, active }: SidebarParagraphProps) {
  return (
    <div
      onClick={onClickAction}
      className="group flex h-11 w-3.5 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors duration-300 hover:bg-primary-500/10 sm:h-5 sm:w-full"
    >
      <span
        className={cn(
          'h-4 w-px rounded-full bg-gray-800/30 transition-colors duration-500 group-hover:bg-primary-500 dark:bg-gray-400 sm:h-px sm:w-3',
          active && 'bg-primary-500 dark:bg-primary-500',
        )}
      ></span>
    </div>
  )
}
